import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';
import { fmtDate, type QuarterlyIndexItem } from './quarterly-export.js';

const emptyStateBox: CSSProperties = {
  background: 'var(--loop-neutral-100)',
  border: '1px solid var(--loop-neutral-200)',
  borderRadius: 'var(--loop-radius-sm)',
  padding: 'var(--loop-space-300)',
  textAlign: 'center',
};


function SignoffCell({ orgs }: { orgs: QuarterlyIndexItem['orgs'] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {orgs.map(o => (
        <span key={o.org} style={{ fontSize: '0.75rem' }}>
          <strong>{o.org}</strong>:{' '}
          {o.signed
            ? <span style={{ color: 'var(--loop-green-700)' }}>Signed by {o.signed_by} ({o.signed_at ? fmtDate(o.signed_at) : ''})</span>
            : <span style={{ color: 'var(--loop-neutral-600)' }}>Not signed</span>}
        </span>
      ))}
    </div>
  );
}

// A quarter with no manifest has two structurally different causes that this
// column previously rendered identically, as a failed generation:
//   - the generation genuinely did not finish (rows written, manifest never
//     reached) — a real, red problem;
//   - the quarter PREDATES the manifest convention (every REVIEW# row still
//     carries the pre-categorisation `completeness` shape). Its generation
//     completed fine. "Generation did not finish" is simply false for it, and
//     this table is the first thing anyone opening the feature sees.
// `completeness_legacy` comes from the index API and separates the two.
function ManifestCell({ manifestPresent, completenessLegacy }: { manifestPresent: boolean; completenessLegacy: boolean }) {
  if (manifestPresent) return <span style={{ color: 'var(--loop-green-700)' }}>Complete</span>;
  if (completenessLegacy) {
    return (
      <span style={{ color: 'var(--loop-yellow-800)' }}>
        Predates the manifest convention — regenerate to complete
      </span>
    );
  }
  return <span style={{ color: 'var(--loop-red-700)' }}>Incomplete — generation did not finish</span>;
}

export function Quarterly() {
  const { data, isLoading, error } = useApiQuery<QuarterlyIndexItem[]>(['quarterly'], '/api/quarterly');
  const quarters = data ?? [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>
          Quarterly Review
        </h2>
      </div>

      {isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
      {error !== null && <p className="error">Error: {String(error)}</p>}

      {data && quarters.length === 0 && (
        <div style={emptyStateBox}>
          <p style={{ fontWeight: 600 }}>No quarterly reviews generated yet.</p>
          <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>
            The Clause 9.1 quarterly review job writes one artifact per quarter, per org.
          </p>
        </div>
      )}

      {quarters.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
            <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
              <tr>
                {['Quarter', 'Period', 'Generated', 'Manifest', 'Sign-off'].map(h => (
                  <th key={h} style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', fontWeight: 528 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {quarters.map(q => (
                <tr key={q.quarter} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                  <td style={{ padding: '8px 16px' }}>
                    <Link to={`/quarterly/${q.quarter}`} style={{ color: 'var(--loop-blue-600)', fontWeight: 528 }}>
                      {q.quarter}
                    </Link>
                  </td>
                  <td style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)' }}>
                    {q.period_start} – {q.period_end}
                  </td>
                  <td style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)' }}>{fmtDate(q.generated_at)}</td>
                  <td style={{ padding: '8px 16px' }}>
                    <ManifestCell manifestPresent={q.manifest_present} completenessLegacy={q.completeness_legacy === true} />
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <SignoffCell orgs={q.orgs} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
